/* eslint-disable no-underscore-dangle */
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { ICellEditor } from "@ag-grid-community/core";
import { styled, muiInputFormsCSS, narrowPadding } from "@iventis/styles";

import { FormControl, ListSubheader, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { IventisTableData, SubheaderListItemCellEditorProps } from "../types/data-table.types";

function _SubheaderListItemCellEditor<TData extends IventisTableData = IventisTableData>(
    { value: inputValue, componentParams, node, column, filterOptions }: SubheaderListItemCellEditorProps<TData>,
    ref: React.ForwardedRef<ICellEditor>
) {
    const { sections } = componentParams;

    const [value, setValue] = useState<string>(inputValue);

    const onSelectionChange = (event: SelectChangeEvent<string>) => {
        setValue(event.target.value);
    };

    useImperativeHandle(ref, () => ({
        getValue() {
            return value || inputValue;
        },
        isCancelBeforeStart() {
            return !node.data.canEdit;
        },
        isCancelAfterEnd() {
            return false;
        },
    }));

    const filteredOptions = useMemo(
        () => sections.map((section) => ({ ...section, listItems: filterOptions(section.listItems, node, column.getColId()) })).filter(({ listItems }) => listItems.length !== 0),
        [sections, filterOptions]
    );

    return (
        <FormControl style={{ width: "100%", height: "100%" }}>
            <StyledSelect
                variant="standard"
                onChange={onSelectionChange}
                value={value}
                autoFocus
                defaultOpen
                MenuProps={{
                    /* AG Grid's popup class will prevent the cell editor closing due to loss of cell focus when the menu appears */
                    PopoverClasses: {
                        root: "ag-custom-component-popup",
                    },
                }}
            >
                {filteredOptions.flatMap(({ title, listItems }) => [
                    <ListSubheader key={title}>{title}</ListSubheader>,
                    listItems.map(({ id, name }) => (
                        <MenuItem value={id} key={id}>
                            {name}
                        </MenuItem>
                    )),
                ])}
            </StyledSelect>
        </FormControl>
    );
}

const StyledSelect = styled(Select)`
    ${muiInputFormsCSS}
    width: 100%;
    height: 100%;

    .MuiSelect-select {
        padding-left: ${narrowPadding};
    }
`;

export const SubheaderListItemCellEditor = forwardRef(_SubheaderListItemCellEditor);
